import { useState, useCallback, useRef, useEffect, useReducer } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Undo2,
  Redo2,
  Link2,
  Unlink,
  Highlighter,
  Type,
  Palette,
  RemoveFormatting,
  ChevronDown,
  Check,
  ExternalLink,
  ImagePlus,
  Sparkles,
  Contrast,
} from 'lucide-react';
import type { Editor } from '@tiptap/core';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useActiveEditor } from '@/store/editorContext';
import { useBlockStore } from '@/store/blockStore';
import { toast } from '@/lib/toast';
import VariablesMenu from '../blocks/VariablesMenu';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const TEXT_COLORS = [
  { label: 'Standaard', value: '', swatch: 'bg-foreground' },
  { label: 'UBO Blauw', value: 'rgb(0, 7, 236)' },
  { label: 'Donker', value: '#050ced' },
  { label: 'Wit', value: '#ffffff' },
  { label: 'Grijs', value: '#6b7280' },
  { label: 'Rood', value: '#dc2626' },
  { label: 'Groen', value: '#16a34a' },
  { label: 'Oranje', value: '#ea580c' },
  { label: 'Paars', value: '#7c3aed' },
];

/** Vaste reeks zodat lettergroottes in offertes consistent blijven (uboTheme body = 16). */
const FONT_SIZES = ['10', '12', '14', '16', '18', '20', '24', '28', '32', '40', '48'];

/** Ingestelde lettergrootte, anders de getoonde grootte van de tekst bij de cursor. */
function currentFontSize(editor: Editor): string | null {
  const explicit = editor.getAttributes('textStyle').fontSize as string | undefined;
  if (explicit) return String(parseFloat(explicit));
  try {
    const { node } = editor.view.domAtPos(editor.state.selection.from);
    const element = node instanceof Element ? node : node.parentElement;
    const size = element ? parseFloat(getComputedStyle(element).fontSize) : NaN;
    return Number.isFinite(size) ? String(Math.round(size)) : null;
  } catch {
    return null;
  }
}

const HIGHLIGHT_COLORS = [
  { label: 'Geen', value: '' },
  { label: 'Geel', value: '#fef08a' },
  { label: 'Blauw', value: '#bfdbfe' },
  { label: 'Groen', value: '#bbf7d0' },
  { label: 'Roze', value: '#fbcfe8' },
  { label: 'Oranje', value: '#fed7aa' },
  { label: 'Paars', value: '#e9d5ff' },
];

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  ariaLabel?: string;
  children: React.ReactNode;
  className?: string;
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  ariaLabel,
  children,
  className,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel ?? title}
      className={cn(
        'inline-flex items-center justify-center h-8 min-w-8 px-1.5 rounded-lg text-sm',
        'transition-colors duration-150 ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
        active
          ? 'bg-primary/10 text-primary'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
        disabled && 'opacity-40 pointer-events-none',
        className
      )}
    >
      {children}
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-5 bg-gray-200 mx-1 shrink-0" />;
}

interface GlobalToolbarProps {
  onToggleAI?: () => void;
  aiOpen?: boolean;
  /** Quote-breed: wit filter op klantlogo bij donkere header (layoutConfig). */
  clientLogoWhiteFilterOnDark?: boolean;
  onClientLogoWhiteFilterOnDarkChange?: (next: boolean) => void;
  /** Annuleert debounced TipTap→blocks sync vóór undo/redo (voorkomt stale state). */
  onBeforeUndoRedo?: () => void;
}

export default function GlobalToolbar({
  onToggleAI,
  aiOpen,
  clientLogoWhiteFilterOnDark,
  onClientLogoWhiteFilterOnDarkChange,
  onBeforeUndoRedo,
}: GlobalToolbarProps = {}) {
  const { activeEditor: editor } = useActiveEditor();
  const structuralUndoDepth = useBlockStore((s) => s.blockHistoryPast.length);
  const structuralRedoDepth = useBlockStore((s) => s.blockHistoryFuture.length);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [, refreshToolbar] = useReducer((value) => value + 1, 0);
  useEffect(() => {
    if (!editor) return;
    editor.on('transaction', refreshToolbar);
    return () => {
      editor.off('transaction', refreshToolbar);
    };
  }, [editor]);

  useEffect(() => {
    if (linkOpen && linkInputRef.current) {
      linkInputRef.current.focus();
    }
  }, [linkOpen]);

  const ensureCursorInSection = useCallback((): boolean => {
    if (!editor) return false;

    const SECTION_TYPES = new Set(['contentPageSection', 'planVanAanpakSection']);
    const { selection } = editor.state;
    for (let d = selection.$anchor.depth; d >= 1; d--) {
      if (SECTION_TYPES.has(selection.$anchor.node(d).type.name)) return true;
    }

    let sectionPos: number | null = null;
    editor.state.doc.descendants((node, pos) => {
      if (sectionPos !== null) return false;
      if (SECTION_TYPES.has(node.type.name)) {
        sectionPos = pos + 1;
        return false;
      }
      return true;
    });

    if (sectionPos !== null) {
      editor.commands.focus(sectionPos);
      return true;
    }
    return false;
  }, [editor]);

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !editor) return;

      if (!ensureCursorInSection()) {
        toast.error('Voeg eerst een tekst-pagina toe om een afbeelding in te voegen');
        if (imageInputRef.current) imageInputRef.current.value = '';
        return;
      }

      setIsUploading(true);
      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const ok = (editor.chain().focus() as any).setImage({ src: dataUrl }).run();
        if (!ok) {
          toast.error('Kon de afbeelding niet invoegen op deze positie');
        }
      } catch (err) {
        console.error('Image upload failed:', err);
        toast.error('Afbeelding invoegen mislukt', { error: err });
      } finally {
        setIsUploading(false);
        if (imageInputRef.current) imageInputRef.current.value = '';
      }
    },
    [editor, ensureCursorInSection]
  );

  const setLink = useCallback(() => {
    if (!editor) return;
    if (!linkUrl) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setLinkUrl('');
    setLinkOpen(false);
  }, [editor, linkUrl]);

  const clientLogoFilterControl =
    onClientLogoWhiteFilterOnDarkChange != null && clientLogoWhiteFilterOnDark != null ? (
      <div
        className="flex items-center gap-1.5 shrink-0 px-1"
        title="Wit klantlogo op donkere pagina’s (links in de header)"
      >
        <Contrast className="h-3.5 w-3.5 text-gray-500 shrink-0" aria-hidden />
        <Label
          htmlFor="toolbar-client-logo-white"
          className="text-[11px] text-gray-600 cursor-pointer whitespace-nowrap hidden sm:inline"
        >
          Wit klantlogo
        </Label>
        <Switch
          id="toolbar-client-logo-white"
          checked={clientLogoWhiteFilterOnDark}
          onCheckedChange={onClientLogoWhiteFilterOnDarkChange}
          className="scale-90"
        />
      </div>
    ) : null;

  if (!editor) {
    return (
      <div className="flex h-11 items-center border-b border-gray-200 bg-white px-3 gap-3">
        {clientLogoFilterControl}
        <span className="text-[13px] text-gray-400">
          Klik in een tekstveld om de toolbar te activeren
        </span>
      </div>
    );
  }

  const currentHeading = editor.isActive('heading', { level: 1 })
    ? 'Heading 1'
    : editor.isActive('heading', { level: 2 })
      ? 'Heading 2'
      : editor.isActive('heading', { level: 3 })
        ? 'Heading 3'
        : 'Tekst';
  const explicitFontSize = editor.getAttributes('textStyle').fontSize as string | undefined;
  const fontSize = currentFontSize(editor);

  return (
    <div
      data-editor-toolbar="true"
      className="flex shrink-0 h-10 lg:h-11 items-center gap-0.5 border-b border-gray-200 bg-white px-2 lg:px-3 overflow-x-auto scrollbar-none"
    >
      {/* Undo / Redo */}
      <ToolbarButton
        onClick={() => {
          onBeforeUndoRedo?.();
          if (editor.can().undo()) {
            editor.chain().focus().undo().run();
          } else {
            void useBlockStore.getState().undoBlocks();
          }
        }}
        disabled={!editor.can().undo() && structuralUndoDepth === 0}
        title="Ongedaan maken (⌘Z / Ctrl+Z)"
        ariaLabel="Ongedaan maken"
      >
        <Undo2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => {
          onBeforeUndoRedo?.();
          if (editor.can().redo()) {
            editor.chain().focus().redo().run();
          } else {
            void useBlockStore.getState().redoBlocks();
          }
        }}
        disabled={!editor.can().redo() && structuralRedoDepth === 0}
        title="Opnieuw (⌘⇧Z / Ctrl+Y)"
        ariaLabel="Opnieuw"
      >
        <Redo2 className="h-4 w-4" />
      </ToolbarButton>

      {editor.isEditable && (
        <>
          <ToolbarSeparator />

          {/* Heading Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Tekststijl: ${currentHeading}`}
                className={cn(
                  'inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-sm font-medium',
                  'transition-colors duration-150 ease-out',
                  'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30'
                )}
              >
                <span className="text-[13px]">{currentHeading}</span>
                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem
                onClick={() => editor.chain().focus().setParagraph().run()}
                className="flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <Type className="h-4 w-4 text-gray-400" />
                  <span>Tekst</span>
                </span>
                {!editor.isActive('heading') && <Check className="h-3.5 w-3.5 text-primary" />}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                className="flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <Heading1 className="h-4 w-4 text-gray-400" />
                  <span className="text-lg font-bold">Heading 1</span>
                </span>
                {editor.isActive('heading', { level: 1 }) && (
                  <Check className="h-3.5 w-3.5 text-primary" />
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                className="flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <Heading2 className="h-4 w-4 text-gray-400" />
                  <span className="text-base font-bold">Heading 2</span>
                </span>
                {editor.isActive('heading', { level: 2 }) && (
                  <Check className="h-3.5 w-3.5 text-primary" />
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                className="flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <Heading3 className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-bold">Heading 3</span>
                </span>
                {editor.isActive('heading', { level: 3 }) && (
                  <Check className="h-3.5 w-3.5 text-primary" />
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Font size */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title="Lettergrootte"
                aria-label={`Lettergrootte: ${fontSize ?? 'standaard'}`}
                className={cn(
                  'inline-flex items-center gap-1 h-8 px-2 rounded-lg text-sm font-medium',
                  'transition-colors duration-150 ease-out',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                  explicitFontSize
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                )}
              >
                <span className="text-[13px] tabular-nums min-w-[1.25rem] text-center">
                  {fontSize ?? '–'}
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-36">
              <DropdownMenuItem
                onClick={() => editor.chain().focus().unsetFontSize().run()}
                className="flex items-center justify-between"
              >
                <span>Standaard</span>
                {!explicitFontSize && <Check className="h-3.5 w-3.5 text-primary" />}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {FONT_SIZES.map((size) => (
                <DropdownMenuItem
                  key={size}
                  onClick={() => editor.chain().focus().setFontSize(`${size}px`).run()}
                  className="flex items-center justify-between tabular-nums"
                >
                  <span>{size}</span>
                  {explicitFontSize === `${size}px` && (
                    <Check className="h-3.5 w-3.5 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <ToolbarSeparator />

          {/* Inline formatting */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Vet (⌘B)"
          >
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Cursief (⌘I)"
          >
            <Italic className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')}
            title="Onderstrepen (⌘U)"
          >
            <Underline className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarSeparator />

          {/* Text color */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'inline-flex items-center gap-0.5 h-8 px-1.5 rounded-lg text-sm',
                  'transition-colors duration-150 ease-out',
                  'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  'focus-visible:outline-none'
                )}
                title="Tekstkleur"
              >
                <Palette className="h-4 w-4" />
                <ChevronDown className="h-3 w-3 text-gray-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="start" sideOffset={8}>
              <p className="text-xs font-medium text-gray-500 mb-2">Tekstkleur</p>
              <div className="grid grid-cols-5 gap-1.5">
                {TEXT_COLORS.map((color) => (
                  <button
                    key={color.value || 'default'}
                    type="button"
                    aria-label={`Tekstkleur: ${color.label}`}
                    className={cn(
                      'w-7 h-7 rounded-lg border-2 transition-all hover:scale-110',
                      !color.value
                        ? 'border-gray-200 bg-gradient-to-br from-gray-100 to-gray-400'
                        : 'border-transparent hover:border-gray-300'
                    )}
                    style={color.value ? { backgroundColor: color.value } : undefined}
                    title={color.label}
                    onClick={() => {
                      if (!color.value) {
                        editor.chain().focus().unsetColor().run();
                      } else {
                        editor.chain().focus().setColor(color.value).run();
                      }
                    }}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Highlight */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'inline-flex items-center gap-0.5 h-8 px-1.5 rounded-lg text-sm',
                  'transition-colors duration-150 ease-out',
                  editor.isActive('highlight')
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  'focus-visible:outline-none'
                )}
                title="Markeren"
              >
                <Highlighter className="h-4 w-4" />
                <ChevronDown className="h-3 w-3 text-gray-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="start" sideOffset={8}>
              <p className="text-xs font-medium text-gray-500 mb-2">Markeerkleur</p>
              <div className="flex gap-1.5">
                {HIGHLIGHT_COLORS.map((color) => (
                  <button
                    key={color.value || 'none'}
                    type="button"
                    aria-label={`Markeerkleur: ${color.label}`}
                    className={cn(
                      'w-7 h-7 rounded-lg border-2 transition-all hover:scale-110',
                      !color.value
                        ? 'border-gray-200 bg-white flex items-center justify-center text-gray-400 text-xs'
                        : 'border-transparent hover:border-gray-300'
                    )}
                    style={color.value ? { backgroundColor: color.value } : undefined}
                    title={color.label}
                    onClick={() => {
                      if (!color.value) {
                        editor.chain().focus().unsetHighlight().run();
                      } else {
                        editor.chain().focus().toggleHighlight({ color: color.value }).run();
                      }
                    }}
                  >
                    {!color.value && '✕'}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <ToolbarSeparator />

          {/* Lists */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="Opsomming"
          >
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title="Genummerde lijst"
          >
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarSeparator />

          {/* Alignment */}
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            active={editor.isActive({ textAlign: 'left' })}
            title="Links uitlijnen"
          >
            <AlignLeft className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            active={editor.isActive({ textAlign: 'center' })}
            title="Centreren"
          >
            <AlignCenter className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            active={editor.isActive({ textAlign: 'right' })}
            title="Rechts uitlijnen"
          >
            <AlignRight className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            active={editor.isActive({ textAlign: 'justify' })}
            title="Uitvullen"
          >
            <AlignJustify className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarSeparator />

          {/* Blockquote */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive('blockquote')}
            title="Citaat"
          >
            <Quote className="h-4 w-4" />
          </ToolbarButton>

          {/* Image upload */}
          <ToolbarButton
            onClick={() => imageInputRef.current?.click()}
            disabled={isUploading}
            title="Afbeelding invoegen"
          >
            <ImagePlus className="h-4 w-4" />
          </ToolbarButton>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />

          {/* Link */}
          <Popover open={linkOpen} onOpenChange={setLinkOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'inline-flex items-center justify-center h-8 min-w-8 px-1.5 rounded-lg text-sm',
                  'transition-colors duration-150 ease-out',
                  'focus-visible:outline-none',
                  editor.isActive('link')
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )}
                title="Link (⌘K)"
                onClick={() => {
                  const existing = editor.getAttributes('link').href;
                  setLinkUrl(existing || '');
                }}
              >
                <Link2 className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start" sideOffset={8}>
              <div className="p-3">
                <p className="text-xs font-medium text-gray-500 mb-2">Link URL</p>
                <div className="flex gap-2">
                  <input
                    ref={linkInputRef}
                    type="url"
                    placeholder="https://..."
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && setLink()}
                    className="flex-1 h-8 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                  />
                  <button
                    onClick={setLink}
                    className="h-8 px-3 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    Toepassen
                  </button>
                </div>
                {editor.isActive('link') && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                    <button
                      className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      onClick={() => {
                        const href = editor.getAttributes('link').href;
                        if (href) window.open(href, '_blank');
                      }}
                    >
                      <ExternalLink className="h-3 w-3" />
                      Openen
                    </button>
                    <button
                      className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 transition-colors"
                      onClick={() => {
                        editor.chain().focus().unsetLink().run();
                        setLinkOpen(false);
                      }}
                    >
                      <Unlink className="h-3 w-3" />
                      Verwijderen
                    </button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Clear formatting */}
          <ToolbarButton
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
            title="Opmaak wissen"
          >
            <RemoveFormatting className="h-4 w-4" />
          </ToolbarButton>
        </>
      )}

      {clientLogoFilterControl && (
        <>
          <ToolbarSeparator />
          {clientLogoFilterControl}
        </>
      )}

      {editor.isEditable && (
        <>
          <ToolbarSeparator />

          {/* Variables */}
          <VariablesMenu
            onInsert={(tag, label) => {
              if ((editor as any).commands.insertVariable) {
                (editor as any).commands.insertVariable({ tag, label });
              } else {
                editor.chain().focus().insertContent(tag).run();
              }
            }}
            buttonVariant="ghost"
          />
        </>
      )}

      {onToggleAI && (
        <>
          <ToolbarSeparator />
          <ToolbarButton
            onClick={onToggleAI}
            active={aiOpen}
            title="AI Assistent"
            className={cn(
              aiOpen && 'bg-[rgb(0,7,236)]/10 text-[rgb(0,7,236)] hover:bg-[rgb(0,7,236)]/15'
            )}
          >
            <Sparkles className="h-4 w-4" />
          </ToolbarButton>
        </>
      )}
    </div>
  );
}
